import Fuse, { IFuseOptions, FuseResult } from 'fuse.js';

const DEFAULT_FUSE_OPTIONS: IFuseOptions<unknown> = {
  threshold: 0.4,
  includeScore: true,
  ignoreLocation: true,
  ignoreFieldNorm: true,
  minMatchCharLength: 2,
};

export interface FuzzyMatcher<T> {
  search: (query: string) => FuseResult<T>[];
  findBestMatch: (query: string, scoreThreshold?: number) => T | null;
}

export function createFuzzyMatcher<T>(
  items: T[],
  keys: string[],
  options?: Partial<IFuseOptions<T>>
): FuzzyMatcher<T> {
  const fuse = new Fuse(items, {
    ...DEFAULT_FUSE_OPTIONS,
    keys,
    ...options,
  } as IFuseOptions<T>);

  return {
    search: (query: string) => fuse.search(query),
    findBestMatch: (query: string, scoreThreshold = 0.4) => {
      const results = fuse.search(query);
      if (
        results.length > 0 &&
        results[0].score !== undefined &&
        results[0].score <= scoreThreshold
      ) {
        return results[0].item;
      }
      return null;
    },
  };
}

// Pre-configured matcher for name-based matching (physicians, insurance, etc.)
export function createNameMatcher<T extends { name: string }>(
  items: T[]
): FuzzyMatcher<T> {
  return createFuzzyMatcher(items, ['name'], { minMatchCharLength: 3 });
}
